import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Drawer,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { AppColors } from '../../../../util/AppColors';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSpecialityList,
  createSpeciality,
  updateSpeciality,
  deleteSpeciality
} from '../../../../redux/reducers/specialitySlice';
import CustomModal from '../../../../components/CustomModal/CustomModal';
import CustomButton from '../../../../components/CustomButton';
function Speciality() {
  const dispatch = useDispatch();

  const [search, setSearch] = useState('');
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState({ roleName: '' });
  const [delModVisible, setDelModVisible] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const specialityList = useSelector((state) => state.speciality.list);

  useEffect(() => {
    dispatch(getSpecialityList());
  }, [dispatch]);

  const openCreateDrawer = () => {
    setForm({ roleName: '' });
    setEditIndex(null);
    setOpenDrawer(true);
  };

  const openEditDrawer = (index) => {
    const item = specialityList[index];
    setForm({ roleName: item.roleName });
    setEditIndex(index);
    setOpenDrawer(true);
  };

  const handleDrawerClose = () => {
    setForm({ roleName: '' });
    setEditIndex(null);
    setOpenDrawer(false);
  };

  const handleAddOrUpdate = () => {
    if (!form.roleName) return;

    if (editIndex !== null) {
      const speciality = specialityList[editIndex];
      dispatch(updateSpeciality({ roleId: speciality.roleId, roleName: form.roleName }))
        .then(() => handleDrawerClose());
    } else {
      dispatch(createSpeciality(form)).then(() => {
        dispatch(getSpecialityList());
        handleDrawerClose();
      });
    }
  };

  const handleDeleteIconClick = (index) => {
    setDeleteIndex(index);
    setDelModVisible(true);
  };

  const handleDelModClose = () => {
    setDelModVisible(false);
    setDeleteIndex(null);
  };

  const handleDelConfirm = () => {
    const speciality = specialityList[deleteIndex];
    dispatch(deleteSpeciality({ roleId: speciality.roleId })).then(() => {
      dispatch(getSpecialityList());
      handleDelModClose();
    });
  };

  const filteredRows = specialityList.filter((row) =>
    row.roleName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box p={3}>
      {/* Top Bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap">
        <Box display="flex" gap={2} mx="auto" mb={2}>
          <TextField
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ width: '500px', backgroundColor: AppColors.lightPink }}
          />
          <Button variant="contained" onClick={openCreateDrawer}>
            Create
          </Button>
        </Box>
        <Typography variant="h6" sx={{ ml: 'auto' }}>
          Total Specialities: {filteredRows.length}
        </Typography>
      </Box>

      {/* Scrollable Table */}
      <Box sx={{ mt: 2, maxHeight: '60vh', overflowY: 'auto' }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Speciality Name</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">No records found</TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row, index) => (
                  <TableRow key={row.roleId}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{row.roleName}</TableCell>
                    <TableCell>{row.userName || '-'}</TableCell>
                    <TableCell>{new Date(row.createdDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <IconButton color="primary" size="small" onClick={() => openEditDrawer(index)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" size="small" onClick={() => handleDeleteIconClick(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Right Drawer (Add/Edit) */}
      <Drawer anchor="right" open={openDrawer} onClose={handleDrawerClose}>
        <Box p={3} width={300}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color="primary">
              {editIndex !== null ? 'Edit Speciality' : 'Create Speciality'}
            </Typography>
            <IconButton onClick={handleDrawerClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            label="Speciality Name"
            value={form.roleName}
            onChange={(e) => setForm({ ...form, roleName: e.target.value })}
            margin="normal"
            InputLabelProps={{
              sx: {
                position: 'absolute',
                top: '50%',
                left: '14px',
                transform: 'translateY(-50%)',
                transition: 'all 0.2s ease-out',
                '&.Mui-focused, &.MuiFormLabel-filled': {
                  top: '0px',
                  transform: 'translateY(-50%) scale(0.75)',
                  transformOrigin: 'top left',
                }
              }
            }}
          />

          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleAddOrUpdate}
          >
            {editIndex !== null ? 'Update' : 'Add'}
          </Button>
        </Box>
      </Drawer>

      {/* Delete Confirmation Modal */}
      <CustomModal
        open={delModVisible}
        onClose={handleDelModClose}
        header="Delete"
        modal
        modalWidth={30}
      >
        <Typography className="flex-center txt-regular fs14">
Are you sure you want to delete this record?        </Typography>

        <div className="flex-center mb10 mt15">
          <div>
            <CustomButton text="Cancel" onClick={handleDelModClose} />
          </div>
          <div className="ml10">
            <CustomButton redBtn text="Confirm" onClick={handleDelConfirm} />
          </div>
        </div>
      </CustomModal>
    </Box>
  );
}

export default Speciality;


